<template>
  <div class="px-0 md:px-[55px] text-left w-full">
    
    <div class="mb-6">
      <h2 class="text-[#FFFFFF] text-m font-bold mb-4">Effective Date: 11/03/2025</h2>
      
      <p class="leading-relaxed description font-regular mb-2">
        Welcome to Sequreio (“Company,” “we,” “our,” or “us”).
      </p>

      <p class="leading-relaxed mb-2 description font-regular">
        At Sequreio, we are committed to protecting your privacy and ensuring the security of your personal data.
      </p>

      <p class="leading-relaxed mb-2 description font-regular">
        This Privacy Policy explains how we collect, use, disclose, and safeguard your information
        when you use our password generation and management application (the “App” or “Services”).
      </p>

      <p class="leading-relaxed mb-2 description font-regular">
        It also outlines your rights and choices regarding your data in compliance
        with the General Data Protection Regulation (GDPR) and other applicable data protection laws.
      </p>

      <p class="leading-relaxed mb-2 description font-regular">
        Sequreio is designed to provide users with a secure platform to generate,
        store, and manage strong, unique passwords for online accounts and services.
        With our advanced filtering and organisational features, users can efficiently manage
        and categorise their credentials, ensuring enhanced security and ease of access.
      </p>

      <p class="leading-relaxed mb-4 description font-regular">
        By using Sequreio, you entrust us with sensitive data, and we take this 
        responsibility seriously. Our app employs industry-standard encryption and security
        protocols to protect your stored data, ensuring that your passwords and personal 
        information remain confidential and secure.
      </p>

      <p class="leading-relaxed mb-2 description font-regular">
       <strong>Key Features of the App Include:</strong>
      </p>

      <ul class="list-disc list-inside ml-1 mb-4 leading-relaxed description font-regular">
        <li class="mb-2"><strong>AES-256 Encryption:</strong> All sensitive data—passwords, usernames, and credentials—is encrypted using military-grade AES-256-GCM encryption directly on your device before being stored or synced.</li>
        <li class="mb-2"><strong>Password Generation:</strong> Create complex, highly secure passwords with customisable length, character types, and complexity settings to protect your accounts.</li>
        <li class="mb-2"><strong>Multi-Factor Authentication:</strong> Add an additional layer of security to your account with TOTP-based two-factor authentication using your preferred authenticator app.</li>
        <li class="mb-2"><strong>Trusted Devices:</strong> Register up to 5 trusted devices to streamline your login experience. Trusted devices can bypass MFA verification while maintaining full security. Instantly revoke access to any device if it is lost or compromised.</li>
        <li class="mb-2"><strong>Secure Storage:</strong> Safely store and encrypt your passwords, usernames, and associated metadata within the app using zero-knowledge architecture.</li>
        <li class="mb-2"><strong>Filtering and Organisation:</strong> Easily categorise passwords into folders and retrieve them based on specific criteria for quick and efficient access.</li>
        <li class="mb-2"><strong>Flexible Sync Modes:</strong> Choose how your data is stored and synchronised—Local Only (offline, on-device storage), Mixed (local with selective cloud backup), or Remote (full cloud sync across all your devices).</li>
        <li class="mb-2"><strong>Cross-Platform Access:</strong> Access your encrypted data securely across multiple devices with end-to-end encryption in transit and at rest.</li>
        <li class="mb-2"><strong>Zero-Knowledge Encryption:</strong> Your encryption keys are derived locally and never leave your device. No one—not even Sequreio—can access your data.</li>
      </ul>

      <p class="leading-relaxed mb-2 description font-regular">
        We value your trust and are dedicated to maintaining transparency about how we handle your data.
      </p>

      <p class="leading-relaxed mb-2 description font-regular">
        This Privacy Policy explains:
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2">What information we collect.</li>
        <li class="mb-2">How we use and process your data.</li>
        <li class="mb-2">Your rights regarding your data.</li>
        <li class="mb-2">How we ensure the security of your information.</li>
      </ul>
      
    </div>

    <div class="space-y-5 text-[#FFFFFF]">
      <h3 class="font-bold text-lg">1. Data Collection and Use</h3>
      <p class="leading-relaxed description font-regular">
        Sequreio is designed to prioritise your privacy and data security.
        We collect only the information necessary to provide and improve our Services,
        ensuring compliance with GDPR and other applicable data protection laws. 
        Below, we outline the types of data we collect, how we collect it, 
        and the purposes for which it is used.
      </p>

      <p class="leading-relaxed description font-regular mb-4">
        A. Types of Data Collected
      </p>

      <h6 class="font-regular text-base mb-2">Personal Information Provided by You</h6>

      <p class="leading-relaxed description font-regular mb-2">
        When you use our App, you may voluntarily provide certain information, including:
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2">Account Information: Email address, username, and password (used for authentication purposes).</li>
        <li class="mb-2">Payment Information (if applicable): Billing address and payment details for 
          premium services, securely processed through third-party payment processors.</li>
        <li class="mb-2">Support Requests: Information shared when you contact us for assistance or inquiries.</li>
      </ul>

      <p class="leading-relaxed description font-regular mb-6">
        <span class="italic">
           <strong>Note:</strong> 
           Payment details are processed securely through trusted third-party providers,
           and we do not store payment information directly.
      </span>
      </p>

      <h6 class="font-regular text-base mb-2">Automatically Collected Information</h6>
  
      <p class="leading-relaxed description font-regular mb-2">
        Certain data is automatically collected to enhance functionality and security:
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"><strong>Device Information:</strong> Device type, operating system, app version, and hardware identifiers.</li>
        <li class="mb-2"><strong>Usage Data: </strong> Features accessed, frequency of usage, error logs, and crash reports.</li>
        <li class="mb-2"><strong>Log Data:</strong> IP address, browser type, and timestamps for server log monitoring and troubleshooting.</li>
      </ul>

      <h6 class="font-regular text-base mb-2">Sensitive Data</h6>
      <p class="leading-relaxed description mb-4 font-regular">
        The app is designed to store and encrypt sensitive information, including passwords and security credentials.
      </p>

      <p class="leading-relaxed description mb-4 font-regular">
        All sensitive data is encrypted locally on your device using AES-256 encryption and is only accessible by you.
      </p>

      <p class="leading-relaxed description mb-4 font-regular">
        We do not collect or process sensitive personal information, such as health data, biometric data, or data concerning racial or ethnic origin.
      </p>

      <p class="leading-relaxed description font-regular mb-4">
        B. Purpose of Data Collection
      </p>

      <p class="leading-relaxed description mb-2 font-regular">
        We collect and process your information to:
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"><strong>Provide Services</strong> - Enable password generation, storage, filtering, and secure access to your data.</li>
        <li class="mb-2"><strong>Secure and Authenticate Accounts</strong> - Verify your identity and protect your data from unauthorised access.</li>
        <li class="mb-2"><strong>Improve User Experience</strong> - Analyse usage patterns to optimise features and resolve technical issues.</li>
        <li class="mb-2"><strong>Customer Support</strong> - Respond to inquiries and provide assistance when needed.</li>
        <li class="mb-2"><strong>Compliance and Legal Obligations</strong> - Ensure compliance with applicable laws and regulations.</li>
        <li class="mb-6"><strong>Marketing and Promotions</strong>  <span class="italic">(Optional, with explicit consent) </span> - Notify you about updates, new features, or special offers.</li>
      </ul>

      <p class="leading-relaxed description font-regular mb-4">
        C. Legal Basis for Data Processing (GDPR Compliance)
      </p>

      <p class="leading-relaxed description mb-4 font-regular">
        We process your personal data based on the following legal grounds:
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"><strong>Consent:</strong> When you explicitly agree to data collection, such as subscribing to marketing communications.</li>
        <li class="mb-2"><strong>Contractual Obligation:</strong> To provide you with the requested Services as agreed upon in our terms of use.</li>
        <li class="mb-2"><strong>Legitimate Interest:</strong> For improving performance, analytics, and customer support, provided such interests do not override your rights.</li>
        <li class="mb-2"><strong>Legal Compliance:</strong> To meet legal requirements and respond to lawful requests from authorities.</li>
      </ul>

      <p class="leading-relaxed description font-regular mb-4">
        D. Optional Permissions
      </p>

      <p class="leading-relaxed description mb-2 font-regular">
        Device Access Permissions:
      </p>
    
      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"><strong>Image Library:</strong> Allows you to choose photo for your personal account.</li>
        <li class="mb-2"><strong>Biometric Authentication:</strong> Allows you to enable fingerprint or facial recognition for quick access.</li>
        <li class="mb-2"><strong>Network Access: </strong> Required for synchronisation between devices and secure cloud backups.</li>
      </ul>


      <h3 class="font-bold text-lg">2. Data Sharing and Third-Party Processing</h3>

      <p class="leading-relaxed description font-regular">
        At Sequreio, we prioritise the confidentiality and security of your data.
        We do not sell, rent, or trade your personal information to third parties. 
        However, we may share certain data with trusted third-party service providers 
        and partners strictly for the purposes outlined below.
      </p>

      <p class="leading-relaxed description font-regular mb-4">
        A. Third-Party Service Providers
      </p>
      
      <p class="leading-relaxed description mb-2 font-regular">
        We work with the following third-party service providers to deliver secure and reliable services:
      </p>

      <p class="leading-relaxed description mb-2 font-regular">
        Backend Services - <strong> Vapor</strong>
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"><strong>Purpose:</strong> Vapor is a server-side Swift framework used to develop and manage our backend infrastructure.</li>
        <li class="mb-2"><strong>Role:</strong> Processes requests, manages authentication, and ensures secure communication between the client and server.</li>
        <li class="mb-2"><strong>Security:</strong> Vapor follows industry standards to secure connections and prevent unauthorized access.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-regular">
        Database - <strong>Neon DB</strong>
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"><strong>Purpose:</strong> Neon DB is used to securely store and manage user data, including encrypted passwords and account details.</li>
        <li class="mb-2"><strong>Role:</strong> Acts as our primary database for structured and encrypted data storage.</li>
        <li class="mb-2"><strong>Security:</strong> Neon DB supports encryption at rest and in transit, along with advanced access controls to protect your data.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-regular">
        Email Service - <strong>Mailgun</strong>
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"><strong>Purpose:</strong>  Mailgun handles email communications, including account verification, password reset emails, and marketing (with explicit consent).</li>
        <li class="mb-2"><strong>Role:</strong> Ensures email delivery and monitors delivery performance.</li>
        <li class="mb-2"><strong>Security:</strong> Mailgun is GDPR-compliant and uses encryption to protect the transmission of data.</li>
      </ul>


      <p class="leading-relaxed description mb-2 font-regular">
        Analytics - <strong>Amplitude</strong>
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"><strong>Purpose:</strong> Amplitude is an analytics platform used to understand how users interact with the App, measure feature adoption, and improve the overall user experience.</li>
        <li class="mb-2"><strong>Data Collected:</strong> Usage events, feature interactions, app version, device type, operating system, and anonymised user identifiers. No passwords, credentials, or sensitive vault data are ever sent to Amplitude.</li>
        <li class="mb-2"><strong>Legal Basis:</strong> Legitimate interest in improving the App's performance and usability.</li>
        <li class="mb-2"><strong>Privacy:</strong> Amplitude is GDPR-compliant and processes data in accordance with its <a href="https://amplitude.com/privacy" target="_blank" class="underline">Privacy Policy</a>. You may opt out of analytics data collection by contacting us at <strong>pearcooperation@gmail.com</strong>.</li>
      </ul>

      <p class="leading-relaxed description font-regular mb-4">
        B. Sharing with Authorities and Legal Compliance
      </p>

      <p class="leading-relaxed description mb-2 font-regular">
        We may disclose your information:
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"><strong>To comply with legal obligations:</strong> In response to legal requests, subpoenas, or court orders.</li>
        <li class="mb-2"><strong>To enforce our terms of service:</strong>  Investigate and address violations or potential threats to security.</li>
        <li class="mb-2"><strong>To prevent fraud or security breaches:</strong> Protect users and the App from malicious activity.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-regular">
        <span class="italic"> We will always evaluate such requests carefully and only disclose information when legally required.</span>
      </p>

      <p class="leading-relaxed description font-regular mb-4">
        C. Data Transfers Outside the EU/EEA
      </p>

      <p class="leading-relaxed description mb-2 font-regular">
        As part of our reliance on Vapor, Neon DB, and Mailgun, your data may be transferred and stored outside the European Economic Area (EEA).
        We ensure these transfers comply with GDPR through:
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"><strong>Standard Contractual Clauses (SCCs)</strong> Legally binding commitments approved by the European Commission.</li>
        <li class="mb-2"><strong>Adequacy Decisions: </strong> Countries deemed to have adequate data protection laws.</li>
        <li class="mb-2"><strong>Binding Corporate Rules (BCRs):</strong> Policies ensuring compliance with GDPR when transferring data between affiliates.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-regular">
        <span class="italic"> You may request further details about these safeguards by contacting us at <strong>pearcooperation@gmail.com</strong></span>
      </p>

      <p class="leading-relaxed description font-regular mb-4">
        D. Aggregated and Anonymised Data
      </p>

      <p class="leading-relaxed description mb-0 font-regular">
        We may use aggregated, non-identifiable data for:
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> Statistical analysis.</li>
        <li class="mb-2"> Business reporting.</li>
        <li class="mb-2"> Service improvements.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-regular">
        <span class="italic">This data does not contain any personal identifiers and cannot be traced back to individual users.</span>
      </p>

      <p class="leading-relaxed description font-regular mb-4">
        E. Third-Party Links and External Websites
      </p>

      <p class="leading-relaxed description mb-2 font-regular">
        Our App may include links to external websites or services, such as help centers, tutorials, or marketing offers. 
        We are not responsible for the privacy practices of these third-party sites and recommend reviewing
        their privacy policies before sharing personal data.
      </p>

      <p class="leading-relaxed description font-regular mb-4">
        F. Security Obligations for Third Parties
      </p>

      <p class="leading-relaxed description mb-2 font-regular">
        All third parties we engage with are required to:
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> Implement security measures equivalent to our own standards (e.g., AES-256 encryption).</li>
        <li class="mb-2"> Process data only for specified purposes and in accordance with our instructions.</li>
        <li class="mb-2"> Notify us immediately in case of a data breach involving your information.</li>
      </ul>

      <h3 class="font-bold text-lg">3. Cookies, Tracking Technologies, and Data Retention</h3>

      <p class="leading-relaxed description mb-2 font-regular">
        At Sequreio, we value transparency about how we handle your data. 
        This section explains our use of cookies, tracking technologies, and our data 
        retention practices to help you understand how your information is processed and stored.
      </p>

      <p class="leading-relaxed description font-regular mb-4">
        A. Cookies and Tracking Technologies
      </p>

      <p class="leading-relaxed description mb-2 font-regular">
        We may use cookies and similar tracking technologies to enhance your experience,
        improve performance, and analyse usage patterns. These technologies allow us to:
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> <strong>Remember your preferences</strong> (e.g., themes or language settings).</li>
        <li class="mb-2"> <strong> Authenticate users</strong> and maintain session security.</li>
        <li class="mb-2"> <strong>Analyse usage patterns</strong> for performance improvements.</li>
        <li class="mb-2"> <strong>Detect and prevent</strong> fraud or security breaches.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-regular">
        Types of Cookies We Use
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> <strong>Essential Cookies</strong> - Necessary for core functionality, such as login and session management.</li>
        <li class="mb-2"> <strong>Analytical Cookies</strong> <span class="italic">(Optional)</span> - Help us understand usage patterns and optimise performance.</li>
        <li class="mb-2"> <strong>Functional Cookies</strong> - for performance improvements.</li>
        <li class="mb-2"> <strong>Marketing Cookies</strong> <span class="italic">(Optional)</span> - fraud or security breaches.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-regular">
        <span class="italic"> Important: You can manage or block cookies through your browser settings. Disabling cookies,
        however, may impact the App's functionality.</span>
      </p>

      <p class="leading-relaxed description font-regular mb-4">
        B. Data Retention Policy
      </p>

      <p class="leading-relaxed description mb-2 font-regular">
        We retain your personal data only for as long as necessary to:
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> Fulfil the purposes outlined in this Privacy Policy.</li>
        <li class="mb-2"> Comply with legal and regulatory obligations.</li>
        <li class="mb-2"> Resolve disputes and enforce our agreements.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-regular">
        Retention Periods:
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> <strong>Account Information</strong> - Stored until you request deletion or deactivate your account.</li>
        <li class="mb-2"> <strong>Transaction and Payment Data </strong> - Retained as required for financial and tax compliance.</li>
        <li class="mb-2"> <strong>Support Communications</strong> -  Retained for up to 12 months for troubleshooting purposes.</li>
        <li class="mb-2"> <strong>Backups</strong> - Stored securely and automatically deleted after 90 days unless required by law.</li>
      </ul>

      <p class="leading-relaxed description font-regular mb-4">
        C. Data Deletion Requests
      </p>

      <p class="leading-relaxed description mb-2 font-regular">
        You have the right to be forgotten under GDPR.
        You may request the deletion of your data at any time by contacting us at <strong>pearcooperation@gmail.com</strong>
        Once we receive and verify your request:
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> 
          We will permanently delete your personal data from our systems.
        </li>
        <li class="mb-2"> 
          Encrypted backups will also be removed within the specified retention period unless required for legal compliance.
        </li>
      </ul>

      <p class="leading-relaxed description mb-2 font-regular">
        <span class="italic"> 
          Note: Some data may need to be retained for regulatory compliance or dispute resolution
        </span>
      </p>

      <p class="leading-relaxed description font-regular mb-4">
        D. Security Measures for Stored Data
      </p>

      <p class="leading-relaxed description mb-2 font-regular">
        To ensure the security of your data, we:
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> Use <strong>AES-256 encryption</strong> to store all sensitive information.</li>
        <li class="mb-2"> Encrypt data in transit and at rest.</li>
        <li class="mb-2"> Implement multi-factor authentication (MFA) for account access.</li>
        <li class="mb-2"> Regularly test and audit security protocols.</li>
        <li class="mb-2"> Limit access to personal data to authorised personnel only.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-regular">
        <span class="italic">Despite our efforts, no system can guarantee 100% security.
          We encourage you to protect your login credentials and use strong, unique passwords.
        </span>
      </p>

      <h3 class="font-bold text-lg">4. User Rights, Data Access, and Controls</h3>
      <p class="leading-relaxed description font-regular">
        At Sequreio, we respect your rights regarding your personal data and provide you with tools 
        to manage your privacy preferences.This section outlines your rights under the
        General Data Protection Regulation (GDPR) and other applicable laws, as well as how to exercise them.
      </p>

      <p class="leading-relaxed description font-regular mb-4">
        A. Your Rights
      </p>

      <p class="leading-relaxed description mb-2 font-semibold">
        Right to Access (Article 15 GDPR)
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> You have the right to request access to the personal data we hold about you.</li>
        <li class="mb-2"> We will provide a copy of your data, free of charge, upon request.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-semibold">
        Right to Rectification (Article 16 GDPR)
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> You may request that we correct or update any inaccurate or incomplete information.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-semibold">
        Right to Erasure (“Right to Be Forgotten”) (Article 17 GDPR)
      </p>

      <p class="leading-relaxed description mb-2 font-regular">
        You can request the deletion of your personal data when:
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> It is no longer necessary for the purposes for which it was collected.</li>
        <li class="mb-2"> You withdraw consent, and no other legal ground for processing exists.</li>
        <li class="mb-2"> Your data has been unlawfully processed.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-semibold">
        Right to Restriction of Processing (Article 18 GDPR)
      </p>
  
      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> You can request that we temporarily stop processing your data under 
          specific circumstances, such as during a dispute regarding its accuracy.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-semibold">
        Right to Data Portability (Article 20 GDPR)
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> You can request to receive your data in a structured, commonly used,
          and machine-readable format and transfer it to another service provider.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-semibold">
        Right to Object (Article 21 GDPR)
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> You may object to the processing of your personal data for direct marketing or profiling purposes at any time.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-semibold">
        Right to Withdraw Consent (Article 7 GDPR)
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> If we process your data based on consent, you can withdraw it at any time without affecting the lawfulness of prior processing.</li>
      </ul>
      
      <p class="leading-relaxed description mb-2 font-semibold">
        Right to Lodge a Complaint (Article 77 GDPR)
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> If you believe your rights have been violated, you may lodge a complaint with your local Data Protection Authority (DPA).</li>
      </ul>

      <p class="leading-relaxed description font-regular mb-4">
       B. Opt-Out and Consent Preferences
      </p>

      <p class="leading-relaxed description mb-2 font-semibold">
        Marketing Communications:
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> For SMS or push notifications, you can manage preferences in your device settings.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-semibold">
        Data Retention Requests:
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> Request data deletion or account closure at any time using the methods listed above.</li>
      </ul>

      <h3 class="font-bold text-lg">5. Data Security Measures and Safeguards</h3>
      
      <p class="leading-relaxed description mb-2 font-semibold">
        At Sequreio, we take the security of your personal data seriously.
        We implement a combination of technical, organisational, and physical security measures to protect your information against
        unauthorised access, alteration, disclosure, or destruction.
      </p>

      <p class="leading-relaxed description font-regular mb-4">
        A. Security Practices
      </p>

      <p class="leading-relaxed description mb-2 font-semibold">
        Encryption Standards
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> <strong>AES-256 Encryption:</strong> All sensitive data, including passwords, is encrypted using AES-256 encryption,
          ensuring it is unreadable to unauthorised parties.</li>
        <li class="mb-2"> <strong>Encryption in Transit and at Rest:</strong> Data is encrypted during transmission and when stored in databases, protecting it at all stages.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-semibold">
        Access Controls
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> <strong>Role-Based Access Control (RBAC):</strong> Access to data is limited to authorised personnel based on their job roles.</li>
        <li class="mb-2"> <strong>Multi-Factor Authentication (MFA):</strong> Administrative access requires MFA to prevent unauthorised logins.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-semibold">
        Secure Hosting Infrastructure
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> Our backend services, powered by Vapor, are hosted on secure servers with regular updates and patches to address vulnerabilities.</li>
        <li class="mb-2"> Data storage in Neon DB benefits from built-in encryption and security monitoring tools.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-semibold">
        Network Security
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> <strong>Firewalls and Intrusion Detection Systems (IDS):</strong> Monitor network traffic for suspicious activities.</li>
        <li class="mb-2"> <strong>Secure Socket Layer (SSL):</strong> Ensures encrypted communication between users and servers.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-semibold">
        Regular Audits and Testing
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> We conduct regular penetration testing and vulnerability assessments to identify and fix potential weaknesses.</li>
        <li class="mb-2"> Security logs are continuously monitored for anomalies and unauthorised activity.</li>
      </ul>

      <p class="leading-relaxed description font-regular mb-4">
        B. Incident Response Plan
      </p>

      <p class="leading-relaxed description mb-2 font-semibold">
        In the unlikely event of a data breach, we have a comprehensive incident response plan to:
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> Contain and mitigate the breach immediately.</li>
        <li class="mb-2"> Assess the scope and impact of the incident.</li>
        <li class="mb-2"> Notify affected users and regulatory authorities (if required) within 72 hours, in compliance with GDPR.</li>
        <li class="mb-2"> Provide recommendations to affected users on protective measures.</li>
      </ul>

      <p class="leading-relaxed description font-regular mb-4">
        C. Third-Party Security Practices
      </p>

      <p class="leading-relaxed description mb-2 font-semibold">
        We require all third-party service providers (e.g., Vapor, Neon DB, Mailgun, and Amplitude) to adhere to strict data security standards, including:
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"><strong>Encryption:</strong> Data must be encrypted both in transit and at rest.</li>
        <li class="mb-2"><strong>Compliance Audits:</strong> Third parties must undergo regular compliance audits to ensure data protection.</li>
        <li class="mb-2"><strong>Incident Reporting:</strong> Providers are required to notify us immediately in case of a security breach.</li>
      </ul>


      <p class="leading-relaxed description font-regular mb-4">
        D. Your Role in Data Security
      </p>

      <p class="leading-relaxed description mb-2 font-semibold">
        While we implement robust security measures, you also play a crucial role in protecting your data. We recommend that you:
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"><strong>Use Strong Passwords:</strong> Create complex passwords and update them regularly.</li>
        <li class="mb-2"><strong>Enable Biometric Authentication:</strong> Use fingerprint or facial recognition features for quick and secure access.</li>
        <li class="mb-2"><strong>Keep Devices Updated:</strong> Ensure your devices and applications are running the latest security patches..</li>
        <li class="mb-2"><strong>Be Aware of Phishing Attacks:</strong> Avoid clicking on suspicious links or providing sensitive information via unsecured channels.</li>
      </ul>

      <p class="leading-relaxed description font-regular mb-4">
        E. Data Anonymisation and Pseudonymisation
      </p>

      <p class="leading-relaxed description mb-2 font-semibold">
        Whenever possible, we use anonymisation and pseudonymisation techniques to protect your data, 
        ensuring that it cannot be linked back to you without additional information.
      </p>


      <h3 class="font-bold text-lg">5A. Subscription Data and Processing</h3>

      <p class="leading-relaxed description font-regular mb-4">
        Sequreio offers premium subscription services managed through Apple's App Store. 
        This section outlines how we collect, process, and manage subscription-related data.
      </p>

      <p class="leading-relaxed description mb-2 font-semibold">
        Subscription Information Collection
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"><strong>Transaction Data:</strong> We receive subscription status, tier (Free/Premium), expiration date, and product identifiers from Apple.</li>
        <li class="mb-2"><strong>Purchase Verification:</strong> Original transaction IDs are stored to verify subscription authenticity and prevent fraud.</li>
        <li class="mb-2"><strong>Payment Processing:</strong> All payment processing is handled exclusively by Apple. We do not collect, store, or have access to your payment card information.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-semibold">
        Purpose of Subscription Data Processing
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"><strong>Feature Access Control:</strong> Determine which features (Trusted Devices, Cross-Device Sync) are available to you based on your subscription tier.</li>
        <li class="mb-2"><strong>Subscription Status Verification:</strong> Continuously validate your subscription status to ensure uninterrupted access to Premium features.</li>
        <li class="mb-2"><strong>Renewal Management:</strong> Track subscription renewal cycles and expiration dates to notify you of upcoming renewals or expirations.</li>
        <li class="mb-2"><strong>Service Continuity:</strong> Manage graceful degradation of service when subscriptions expire, ensuring data retention while limiting feature access.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-semibold">
        Data Retention Upon Subscription Expiration
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"><strong>Account Data:</strong> Your account, passwords, and folders remain intact and encrypted after subscription expiration.</li>
        <li class="mb-2"><strong>Feature Restrictions:</strong> Premium features become inaccessible, but core password management functionality continues.</li>
        <li class="mb-2"><strong>Data Deletion:</strong> Trusted device registrations may be removed after 90 days of subscription lapse unless reactivated.</li>
        <li class="mb-2"><strong>Re-subscription:</strong> All data and Premium features are immediately restored upon subscription renewal.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-semibold">
        Legal Basis for Subscription Processing
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"><strong>Contractual Obligation:</strong> Processing is necessary to fulfill our subscription agreement with you.</li>
        <li class="mb-2"><strong>Legitimate Interest:</strong> Fraud prevention and service optimization justify minimal data retention.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-regular">
        <span class="italic">Note: Subscription purchases are subject to Apple's App Store Terms and Conditions and Privacy Policy.</span>
      </p>


      <h3 class="font-bold text-lg">6. Policy Updates, Notifications, and Version Control</h3>

      <p class = "leading-relaxed description font-regular">
        At Sequreio, we may update this Privacy Policy from time to time to reflect changes in our practices, services, legal requirements, or technological advancements. 
        This section explains how we handle updates, notify users, and maintain version control for full transparency.
      </p>

      <p class="leading-relaxed description mb-2 font-regular">
        A. Policy Updates
      </p>

      <p class="leading-relaxed description mb-2 font-regular">
        We reserve the right to modify this Privacy Policy as necessary to ensure compliance with applicable laws,
        including the General Data Protection Regulation (GDPR), and to align with evolving industry standards and business practices.
      </p>

      <p class="leading-relaxed description mb-2 font-regular">
        When We Update:
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> Changes in data collection or processing practices.</li>
        <li class="mb-2"> Implementation of new features, tools, or services.</li>
        <li class="mb-2"> Amendments required by law or regulatory authorities.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-regular">
        What We Update:
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> Scope of collected data.</li>
        <li class="mb-2"> Use of third-party providers or technologies.</li>
        <li class="mb-2"> Security measures and safeguards.</li>
        <li class="mb-2"> User rights and data control mechanisms.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-regular">
        B. Notification of Changes
      </p>

      <p class="leading-relaxed description mb-2 font-regular">
        We believe in keeping you informed about any significant updates to our Privacy Policy. 
        Notifications will be provided through:
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> In-App Notifications - Alerts displayed within the App upon login.</li>
        <li class="mb-2"> Email Notifications - Sent to the email address associated with your account, especially for material changes.</li>
        <li class="mb-2"> Policy Update Banner - Highlighted banner on our website or application homepage.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-regular">
        Timing of Notifications:
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> Minor changes (e.g., clarifications) may not require prior notification and will be effective immediately upon publication.</li>
        <li class="mb-2"> Major changes (e.g., data-sharing practices or new processing purposes) will include advance notice of at least 30 days before taking effect,
           giving you time to review and understand the updates.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-regular">
        C. Version Control
      </p>

      <p class="leading-relaxed description mb-2 font-regular">
        To ensure complete transparency, we maintain a version control log for this Privacy Policy, documenting:
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> Date of Last Update: Clearly displayed at the top of the Privacy Policy.</li>
        <li class="mb-2"> Version Number: Sequential versioning for easy tracking of changes.</li>
        <li class="mb-2"> Summary of Changes: A brief description of modifications made in each version.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-regular">
        D. Acceptance of Updates
      </p>

      <p class="leading-relaxed description mb-2 font-regular">
        Your continued use of Sequreio following any updates to this Privacy Policy constitutes your acceptance of the revised terms.
        If you do not agree with the changes, you may discontinue using the App and request deletion of your data by contacting us.
      </p>


      <h3 class="font-bold text-lg">7. Children's Privacy</h3>

      <p class="leading-relaxed description font-regular mb-4">
        Sequreio is not directed to children under the age of 13. We do not knowingly collect personal information from individuals under the age of 13. If you are under 13, please do not use the App or provide any information through the App.
      </p>

      <p class="leading-relaxed description font-regular mb-4">
        If we become aware that we have inadvertently collected personal information from a child under 13 without verifiable parental consent, we will take steps to delete that information as soon as possible. If you believe we may have collected information from a child under 13, please contact us immediately at <strong>pearcooperation@gmail.com</strong>.
      </p>

      <p class="leading-relaxed description font-regular mb-6">
        This policy is intended to comply with the Children's Online Privacy Protection Act (COPPA), the General Data Protection Regulation (GDPR), and similar applicable laws globally.
      </p>

      <h3 class="font-bold text-lg">8. Contact Information and Queries</h3>
      <p class="leading-relaxed description font-regular mb-4">
        At Sequreio, we are committed to addressing your questions, concerns, and requests regarding your privacy and data protection rights.
        This section provides the necessary details to contact us and outlines the process for handling inquiries.
      </p>

      <p class="leading-relaxed description mb-2 font-semibold">
        A. Data Protection Officer
      </p>

      <p class="leading-relaxed description mb-6 font-regular">
        Sequreio is a small independent app and does not currently have a formally appointed Data Protection Officer (DPO) as defined under Article 37 GDPR, as we do not engage in large-scale systematic processing of personal data. However, all privacy-related enquiries, data subject requests, and concerns are handled directly by our privacy team and will be responded to with the same diligence. For all privacy matters, please contact us at <strong>pearcooperation@gmail.com</strong>.
      </p>

      <p class="leading-relaxed description mb-2 font-semibold">
        B. Contact Details
      </p>

      <p class="leading-relaxed description mb-6 font-regular">
        For any questions about this Privacy Policy, your data, or to exercise your rights, you can reach us via:<br>
        <strong>Pear Cooperation</strong><br>
        <strong>pearcooperation@gmail.com</strong>
      </p>

      <p class="leading-relaxed description mb-2 font-semibold">
        C. Supervisory Authority (EU/EEA Users)
      </p>

      <p class="leading-relaxed description mb-4 font-regular">
        If you are located in the European Economic Area (EEA) or the United Kingdom and believe we have not handled your personal data in accordance with applicable law, you have the right to lodge a complaint with your local Data Protection Authority (DPA). A list of EU DPAs is available at <a href="https://edpb.europa.eu/about-edpb/about-edpb/members_en" target="_blank" class="underline">edpb.europa.eu</a>. We would, however, appreciate the opportunity to address your concerns directly before you contact a supervisory authority.
      </p>

      <p class="leading-relaxed description mb-2 font-semibold">
        D. Complaints and Dispute Resolution
      </p>

      <p class="leading-relaxed description mb-2 font-regular">
        If you believe your data has been handled inappropriately or your privacy rights have been violated, you may:
      </p>

      <p class="leading-relaxed description mb-2 font-regular">
        Contact Us Directly:
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> We will promptly investigate and respond to your complaint within 30 days.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-regular">
        File a Complaint with Your Data Protection Authority (DPA):
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> If you are located in the European Economic Area (EEA) or United Kingdom, you have the right to lodge a complaint with your local DPA.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-semibold">
        E. Response Timeline for Queries and Requests
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"> We aim to respond to all privacy-related inquiries within 30 days of receipt.</li>
        <li class="mb-2"> For complex cases, this timeline may be extended by up to 60 days, and we will notify you of any delays.</li>
      </ul>
      <h3 class="font-bold text-lg">9. California Privacy Rights (CCPA)</h3>

      <p class="leading-relaxed description font-regular mb-4">
        If you are a California resident, you have specific rights under the California Consumer Privacy Act (CCPA) and the California Privacy Rights Act (CPRA) regarding your personal information.
      </p>

      <p class="leading-relaxed description mb-2 font-semibold">
        Your Rights Under CCPA/CPRA
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"><strong>Right to Know:</strong> You have the right to request disclosure of the categories and specific pieces of personal information we have collected about you, the sources of that information, the purposes for collecting it, and the third parties with whom we share it.</li>
        <li class="mb-2"><strong>Right to Delete:</strong> You have the right to request deletion of personal information we have collected from you, subject to certain exceptions.</li>
        <li class="mb-2"><strong>Right to Correct:</strong> You have the right to request correction of inaccurate personal information we maintain about you.</li>
        <li class="mb-2"><strong>Right to Opt-Out of Sale or Sharing:</strong> We do not sell or share your personal information with third parties for cross-context behavioural advertising.</li>
        <li class="mb-2"><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising any of your CCPA rights.</li>
        <li class="mb-2"><strong>Right to Limit Use of Sensitive Personal Information:</strong> We do not use sensitive personal information (such as your stored passwords or credentials) for any purpose other than providing the App's core functionality.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-semibold">
        Categories of Personal Information Collected
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"><strong>Identifiers:</strong> Email address, account username, and anonymised device identifiers.</li>
        <li class="mb-2"><strong>Usage Data:</strong> App interactions, feature usage, and crash reports collected via Amplitude.</li>
        <li class="mb-2"><strong>Subscription Data:</strong> Subscription status and transaction identifiers received from Apple.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-semibold">
        How to Exercise Your Rights
      </p>

      <p class="leading-relaxed description font-regular mb-6">
        To exercise any of the rights described above, please contact us at <strong>pearcooperation@gmail.com</strong>. We will respond to verified requests within 45 days as required by law. We may need to verify your identity before processing your request.
      </p>

    </div>
  </div>
</template>

<script setup>
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Albert+Sans:wght@300;400;500;600&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap');
</style>

<style scoped> 
.font-light {
  font-family: 'Albert Sans', sans-serif;
  font-weight: 300; /* Light */
}

.font-regular {
  font-family: 'Albert Sans', sans-serif;
  font-weight: 400; /* Regular */
}

.font-regular-inter {
  font-family: 'Inter', sans-serif;
  font-weight: 400; /* Regular */
}

.font-semibold {
  font-family: 'Albert Sans', sans-serif;
  font-weight: 500; /* Semibold */
}

.font-bold {
  font-family: 'Albert Sans', sans-serif;
  font-weight: 600; /* Bold */
}

.description {
    font-size: 14px; /* Smaller text */
    line-height: 24px;
    color: rgba(255, 255, 255, 0.7); /* Slightly faded white */
}
</style>